import { getCountryFormDefinition } from "CMS/utils";
import { FinancialRiskEdit } from "company/detail/components/FinancialRiskEdit";
import { BankFormState, CompanyFormState } from "company/detail/components/models";
import { getInstruction } from "company/detail/utils";
import FinancialRiskCard from "components/FinancialRisk/FinancialRiskCard";
import { FormSection } from "components/MainSectionTemplate/types";
import { GlobalLocationNumberInput } from "components/ValidatedFields/GlobalLocationNumberInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import VatNumberDetails from "components/VatNumber/VatNumberDetails";
import React from "react";
import { isNullOrEmptyString } from "utilities";

export const financialDetailSection = (company: server.dto.Company, companyFormState: CompanyFormState, bankFormState: BankFormState): FormSection => {

  const formDefinition = getCountryFormDefinition(company.admAddress.country);

  return {
    type: "collapsable",
    title: RESX.Financial.FinancialDetails,
    key: "companyFinancialDetails",
    fields: [{
      key: "financialRisk",
      label: RESX.Promoter.FinancialRisk,
      render: () => <FinancialRiskCard financialRisk={companyFormState.financialRisk} />,
      isEmpty: companyFormState.financialRisk.level.value === "unknown",
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.financialRisk,
        render: ({ value, onChange }) => (
          <FinancialRiskEdit
            value={value}
            onChange={onChange}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.FinancialRisk) => {
          const instruction = getInstruction(company, companyFormState, bankFormState, {
            financialRisk: {
              comment: value.level.value === "unknown" ? null : value.comment,
              level: value.level.value
            }
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "euVatNumber",
      label: formDefinition.euVatNumber.label,
      sublabel: formDefinition.euVatNumber.sublabel,
      hidden: !formDefinition.euVatNumber.display,
      render: () => <VatNumberDetails companyId={company.companyId} euVatNumber={company.euVatNumber} />,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.euVatNumber,
        useCustomSavingState: true,
        render: ({ value, onChange, submit, cancel, isSaving }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxEuVatNumberLength}
            onEnter={submit}
            onEsc={cancel}
            loading={isSaving}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(company, companyFormState, bankFormState, { euVatNumber: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      },
    },
    {
      key: "fiscalNumber1",
      label: formDefinition.fiscalNumber1.label,
      sublabel: formDefinition.fiscalNumber1.sublabel,
      render: () => <div>{companyFormState.fiscalNumber1}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.fiscalNumber1),
      hidden: !formDefinition.fiscalNumber1.display,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.fiscalNumber1,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxFiscalNumber1Length}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(company, companyFormState, bankFormState, { fiscalNumber1: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "fiscalNumber2",
      label: formDefinition.fiscalNumber2.label,
      sublabel: formDefinition.fiscalNumber2.sublabel,
      render: () => <div>{companyFormState.fiscalNumber2}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.fiscalNumber2),
      hidden: !formDefinition.fiscalNumber2.display,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.fiscalNumber2,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxFiscalNumber2Length}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(company, companyFormState, bankFormState, { fiscalNumber2: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "legalStructure",
      label: formDefinition.legalStructure.label,
      sublabel: formDefinition.legalStructure.sublabel,
      render: () => <div>{companyFormState.legalStructure}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.legalStructure),
      hidden: !formDefinition.legalStructure.display,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.legalStructure,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxLegalStructureLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(company, companyFormState, bankFormState, { legalStructure: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "businessRegistrationNumber",
      label: formDefinition.businessRegistrationNumber.label,
      sublabel: formDefinition.businessRegistrationNumber.sublabel,
      render: () => <div>{companyFormState.businessRegistrationNumber}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.businessRegistrationNumber),
      hidden: !formDefinition.businessRegistrationNumber.display,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.businessRegistrationNumber,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxBusinessRegistrationNumberLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(company, companyFormState, bankFormState, { businessRegistrationNumber: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "globalLocationNumber",
      label: "Global Location Number (GLN)",
      render: () => <div>{companyFormState.globalLocationNumber}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.globalLocationNumber),
      inPageEdit: {
        initialValue: companyFormState.globalLocationNumber,
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => (
          <GlobalLocationNumberInput
            focusOnMount
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value) => {
          const instruction = getInstruction(company, companyFormState, bankFormState, {
            globalLocationNumber: value
          });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "internalId",
      label: RESX.Company.CustomerNumber,
      isEmpty: isNullOrEmptyString(companyFormState.internalId),
      render: () => <div>{companyFormState.internalId}</div>,
      inPageEdit: {

        initialValue: companyFormState.internalId,
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            maxLength={serverReference.constants.company.maxInternalIdLength}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value) => {
          const instruction = getInstruction(company, companyFormState, bankFormState, {
            internalId: value
          });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "expectPurchaseOrderNumber",
      label: RESX.Company.ExpectsPONumber,
      hidden: !site.currentEnvironment.purchaseOrderNumber,
      render: () => <YesNoText value={companyFormState.expectPurchaseOrderNumber} />,
      inPageEdit: {
        initialValue: companyFormState.expectPurchaseOrderNumber,
        render: ({ value, onChange }) => (
          <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />
        ),
        fullWidth: true,
        type: "custom",
        submitChange: (value: boolean) => {
          const instruction = getInstruction(company, companyFormState, bankFormState, {
            expectPurchaseOrderNumber: value
          });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }]
  };
}